import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { COLORS } from '@livecommerce/design-system';
import { ONE_TO_ONE_PRODUCT, ONE_TO_MANY_PRODUCT, ON_DEMAND_PRODUCT } from 'constants/products.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import useOrgId from 'hooks/useOrgId.js';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import { isProduction } from 'constants/gcp.js';
import useOrgType from 'hooks/useOrgType.js';
import { FeedbackExtension } from './feedbackExtension.js';

const voiceflowProjectIds = {
  [ONE_TO_ONE_PRODUCT]: '6641e7a8f009f48ec9ea429e',
  [ONE_TO_MANY_PRODUCT]: '662f9627ef66b8f006b03486',
  [ON_DEMAND_PRODUCT]: '672e13fd6a18eb2f9c0d42dc',
  ALL_IN_ONE: '6756f8096373f7a4bdb1b742',
};

const BOT_AVATAR_URL = 'https://cdn.liveshopping.bambuser.com/misc/ai-chatbot/bam-ai.svg';
const BAMBUSER_LOGO_URL = 'https://cdn.liveshopping.bambuser.com/misc/ai-chatbot/bam-logo.svg';
const BOT_LAUNCHER_ICON_URL = 'https://cdn.liveshopping.bambuser.com/misc/ai-chatbot/bam-ai.svg';

const SCRIPT_ID = 'voiceflow-script';
const SCRIPT_SRC = 'https://cdn.voiceflow.com/widget-next/bundle.mjs';

const createOrReuseScript = (onLoad) => {
  let voiceflowScript = document.getElementById(SCRIPT_ID);

  if (voiceflowScript?.onload) {
    voiceflowScript.onload = onLoad;
    onLoad();
    return;
  }

  voiceflowScript = document.createElement('script');
  voiceflowScript.id = SCRIPT_ID;
  voiceflowScript.src = SCRIPT_SRC;
  voiceflowScript.type = 'text/javascript';
  voiceflowScript.onload = onLoad;
  document.head.appendChild(voiceflowScript);
};

/**
 * Remove old feedback elements from the chat widget.
 * This can be used at the time of loading the chat widget.
 */
function removeOldFeedbackElements() {
  const chatWidget = document
    .querySelector('#voiceflow-chat')
    .shadowRoot.querySelector('.vfrc-widget:last-child .vfrc-chat--dialog');
  // Old or completed feedback widgets do not include any div with 'vfrc-feedback' class
  const oldFeedbackWidgets = chatWidget?.querySelectorAll('.vfrc-message--extension-Feedback:not(:has(.vfrc-feedback))');
  if (oldFeedbackWidgets) {
    oldFeedbackWidgets.forEach((feedback) => {
      const messageContainer = feedback?.closest('.vfrc-system-response');
      messageContainer && messageContainer?.remove();
    });
  }
}

// https://github.com/voiceflow/react-chat/blob/master/packages/react-chat/src/styles.css
const cssOverrides = `
  .vfrc-launcher {
    display: none;
  }

  .vfrc-avatar {
    background-color: ${COLORS.dark_900};
  }

  .vfrc-system-response .vfrc-avatar {
    background-color: ${COLORS.white};
    display: none;
  }

  .vfrc-system-response--actions:has(> .vfrc-button) {
    padding-left: var(--space-5);
  }

  .vfrc-system-response--actions{
    flex-direction: row;
    >div button {
      background-color: ${COLORS.orange_500};
      color: ${COLORS.white};
    }
  }

  .vfrc-chat {
    background-color: ${COLORS.dark_600};
    .vfrc-header {
      background-color: ${COLORS.black};
    }
    .vfrc-footer {
      background-color: ${COLORS.black};
      color: ${COLORS.white}
      div:has(.vfrc-input-container) {
        background: ${COLORS.dark_600};
      }
    }
  }

  .vfrc-chat .vfrc-footer div:has(.vfrc-input-container) {
    background: ${COLORS.dark_600};
    padding-top: 10px;
    box-shadow: 0px 0px 50px 0px ${COLORS.dark_600};
  }

  .vfrc-widget>div:has(.vfrc-chat) {
    justify-content: center;
  }

  .vfrc-assistant-info {
    & .vfrc-assistant-info--title {
      color: ${COLORS.white};
      display: none;
    }

    & .vfrc-avatar {
      background-color: ${COLORS.white};
      display: none;
    }
  }

  .vfrc-user-response .vfrc-message {
    background-color: ${COLORS.dark_900};
    color: ${COLORS.white};

    & li:not(:last-child) {
      margin-bottom: 0.5em;
    }
  }

  .vfrc-system-response .vfrc-message {
    background-color: ${COLORS.light_100};

    & li:not(:last-child) {
      margin-bottom: 0.5em;
    }
  }

  .vfrc-timestamp {
    display: none;
  }
  
  .vfrc-message {
    margin-right: -10px !important;
  }
  
  .vfrc-system-response--list {
    margin-right: 0px !important;
  }
  
  .vfrc-system-response--message {
    margin-right: 0px !important;
  }
  
  .vfrc-user-response--row {
    margin-right: 0px !important;
    flex-direction: row-reverse !important;
    margin-right: 0px !important;
  }
  
  [class^="vfrc-message--extension-"],
  [class*=" vfrc-message--extension-"] {
    padding: 0px !important;
  }
  
  .vfrc-message--extension-Feedback {
    background: none !important;
  }

  .vfrc-chat--dialog {
    div {
      &:has(+ .vfrc-system-response--actions) {
        padding: 10px;
        margin-bottom: 0;
        background-color: ${COLORS.orange_500};
        border-top-right-radius: 5px;
        border-top-left-radius: 5px;
        div {
          width: 100%;
          padding: 2px;
          background-color: transparent;
          color: white;
        }
      }

      & + .vfrc-system-response--actions {
        background-color: ${COLORS.orange_500}; // Same color to match the previous div
        padding: 10px;
        border-radius: 5px;
        margin-top: 0;
        border-top-right-radius: 0;
        border-top-left-radius: 0;
        padding-left: 16px;
        padding-bottom: 11px;
        button.vfrc-button {
          background-color: ${COLORS.dark_900};
          color: #FFFFFF;
        }
      }
    }
  }
  `;

const useVoiceFlow = () => {
  const [ initialized, setInitialized ] = useState(!!window.voiceflow?.chat);

  // Load the VoiceFlow chat library and wait for it to be initialized
  useEffect(() => {
    if (!initialized) {
      createOrReuseScript(() => {
        const checkLibraryLoaded = () => {
          if (window.voiceflow?.chat) {
            setInitialized(true);
            return;
          }
          setTimeout(checkLibraryLoaded, 10);
        };
        checkLibraryLoaded();
      });
    }
  }, [initialized]);

  return window.voiceflow?.chat;
};

const getVoiceFlowConfig = ({ orgId, userId, userEmail, projectId, selectedProduct, orgType }, onInitialized) => {
  return {
    verify: { projectID: projectId },
    // userID: userId,
    versionID: isProduction ? 'production' : 'development',
    user: {
      name: userEmail || `OrgID: ${orgId}, userID: ${userId}`,
    },
    assistant: {
      renderMode: 'popover',
      // title: 'AI Assistant (BETA)', // To replace the title on the Voiceflow dashboard
      // description: 'I helps you find the answer to your questions.', // To replace the description on the Voiceflow dashboard
      image: BOT_AVATAR_URL,
      header: {
        hideImage: false,
        imageUrl: BOT_LAUNCHER_ICON_URL,
        title: 'AI Assistant (BETA)',
      },
      watermark: false,
      color: COLORS.dark_900,
      position: 'right',
      extensions: [FeedbackExtension],
      stylesheet: `data:text/css;base64,${window.btoa(cssOverrides)}`,
      persistence: 'localStorage',
    },
    launch: {
      event: {
        type: 'launch',
        payload: {
          orgId,
          userId,
          internalUserEmail: userEmail, // Only internal user email
          selectedProduct,
          pageUrl: window?.location.href,
          organizationType: orgType || 'default',
        },
      },
    },
  };
};

const getVoiceflowProjectId = (selectedProduct) =>
  voiceflowProjectIds.ALL_IN_ONE || voiceflowProjectIds[selectedProduct] || null;

const useChatBot = () => {
  const { features } = useFeatureContext();
  const { aiChatBotEnabled } = features;
  const orgId = useOrgId();
  const { selectedProduct } = useProductDataOutsideRoutes();
  const uid = useSelector((state) => state.sessionState.authUser?.uid);
  const internalUserEmail = useSelector((state) => {
    const email = state.sessionState.authUser?.email;
    return email && email.endsWith('@bambuser.com') ? email : null;
  });
  const [ isOpen, setIsOpen ] = useState(false);
  const [ isLoaded, setIsLoaded ] = useState(false);
  // State to track the selected product currently known by the chatbot
  const [ cachedSelectedProduct, setCachedSelectedProduct ] = useState(selectedProduct);
  const client = useVoiceFlow();
  const orgType = useOrgType();

  useEffect(() => {
    if (!aiChatBotEnabled) return;
    if (!uid || !orgId || !client) return;
    if (isLoaded) return;
    const projectId = getVoiceflowProjectId(selectedProduct);
    const config = getVoiceFlowConfig({ orgId, userId: uid, userEmail: internalUserEmail, projectId, selectedProduct, orgType });
    const loadPromise = client.load(config);
    loadPromise.then(() => {
      setIsLoaded(true);
    });
  }, [aiChatBotEnabled, selectedProduct, uid, orgId, client, orgType]);

  // Listen for messages from the AI ChatBot
  useEffect(() => {
    if (!client) return;
    const onMessage = (message) => {
      if (message?.data === '{"type":"voiceflow:close"}') {
        setIsOpen(false);
      }
      if (message?.data === '{"type":"voiceflow:open"}') {
        removeOldFeedbackElements(); // Feedback widget (extension) leaves some empty message boxes
        setIsOpen(true);
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [client]);

  const open = useCallback(() => {
    // Initialize or update cachedSelectedProduct state ONLY when opening the chatbot!
    if (!cachedSelectedProduct) setCachedSelectedProduct(selectedProduct);
    if (cachedSelectedProduct !== selectedProduct) setCachedSelectedProduct(selectedProduct);

    client.open();
    trackingService.track({ eventType: 'chat-bot:open' });
  }, [client, selectedProduct, cachedSelectedProduct]);

  const close = useCallback(() => {
    client.close();
    trackingService.track({ eventType: 'chat-bot:close' });
  }, [client]);

  // Close the chat bot when we change products
  useEffect(() => {
    if (client) {
      client.close();
    }
  }, [selectedProduct, client]);

  // Send switchedProduct event to Voiceflow runtime ONLY when cachedSelectedProduct changes
  // (to avoid sending the same event multiple times which consumes credits)
  useEffect(() => {
    if (!isOpen) return;
    if (!cachedSelectedProduct) return;
    window?.voiceflow?.chat?.interact({
      type: 'event',
      payload: {
        event: {
          name: 'switchedProduct', // The event name defined in your Event CMS
          data: {
            selectedProduct: cachedSelectedProduct,
          },
        },
      },
    });
  }, [cachedSelectedProduct, isOpen]);

  return {
    isOpen,
    isInitialized: !!client,
    open,
    close,
  };
};

export default useChatBot;
